<template>
  <b-container id="app" class="p-0" fluid>
    <div v-if="loginErrorOidc">
      <!-- OIDC制限認証エラー -->
      <login-error-oidc />
    </div>
    <div v-else class="d-flex flex-column vh-100 app-container">
      <router-view name="nav" />

      <loading
        v-show="loading"
        :msg="!!loadingMessage ? loadingMessage : undefined"
        :background="indicatorBackground"
      />
      <div class="d-flex flex-grow-1 app-body">
        <div
          v-if="sideMenu"
          class="p-0 d-none d-md-block d-lg-block d-xl-block side-menu-container"
        >
          <router-view name="sidemenu" />
        </div>
        <router-view v-if="searchWord" name="search" />
        <router-view v-else />
      </div>
    </div>
    <photo-stream
      v-if="photoStreamModal"
      :files="photoStreamArgs.files"
      :param="{ messageId: photoStreamArgs.messageId, commentId: photoStreamArgs.commentId }"
      :downloadableList="downloadable"
      :allowSaveAttachments="allowSaveAttachments"
      :clientType="clientType"
      :isMobile="isMobile"
      v-on:closeModal="closePhotoStream"
    />
    <!-- Url Copy Toast -->
    <b-toast
      v-if="lazyLoad"
      id="urlToast"
      variant="info"
      solid
      no-close-button
      auto-hide-delay="2000"
      :is-status="true"
    >
      <div div class="url-copy-toast">
        <b-icon icon="info-circle" class="mr-2" font-scale="3" /> URLをコピーしました
      </div>
    </b-toast>

    <!-- カテゴリー更新注意アラート -->
    <!-- 5/11 現在使用していない -->
    <alert-modal
      v-if="lazyLoad"
      id="category-update-alert"
      msg="カテゴリーが編集されています。保存しますか？"
      v-on:clickedOK="saveCategoryEdit"
    />

    <alert-modal
      v-if="lazyLoad"
      id="alert-modal"
      :msg="alertModalArgs.msg"
      :help="alertModalArgs.isHelp"
      :helpContentArgs="alertModalArgs.helpContentArgs"
      :hideCancel="alertModalArgs.hideCancel"
      :okLabel="alertModalArgs.okLabel"
      :okVariant="alertModalArgs.okVariant"
      v-on:clickedOK="alertModalArgs.clickedOK"
      v-on:clickedCancel="alertModalArgs.clickedCancel"
    />

    <error-modal
      id="error-modal"
      :msg="errorModalArgs.msg"
      v-on:hideModal="errorModalArgs.afterProcess"
    />

    <not-allowed-modal v-if="lazyLoad" v-bind="notAllowedModalArgs" />

    <favorite-modal v-if="lazyLoad" v-bind="favoriteModalArgs" />

    <domain-setting v-if="lazyLoad" v-bind="domainSettingArgs" />

    <sort-comment-setting v-if="lazyLoad" v-bind="sortCommentSettingArgs" />

    <acl-edit-modal v-if="lazyLoad" v-bind="aclEditModalArgs" />

    <action-sheet v-if="lazyLoad" />
  </b-container>
</template>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  width: 100%;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

.app-contaienr {
  overflow: hidden;
}
.app-container .app-body {
  height: 100%;
  overflow: hidden;
}

.side-menu-container {
  width: 300px;
  min-width: 300px;
  height: auto;
  min-height: auto;
  background-color: #ecf1f4;
}

/* PC用画面 */
@media (min-width: 768px) {
  .app-container .app-body {
    overflow: hidden;
  }
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  ref,
} from "vue";
import type Vue from "vue";
import { Route } from "vue-router";

import AppAuth from "./aws-config";
// import * as AmplifyModules from "aws-amplify";

// @ts-ignore
// import { AmplifyPlugin } from "aws-amplify-vue";
// Vue.use( AmplifyPlugin, AmplifyModules )

import type { FavoriteModalUser } from "./model";
import { Category, Topic, Message, Comment, User, Domain } from "./model";

import DomainSetting from "./views/DomainSetting.vue";
import LoginErrorOidc from "./views/LoginErrorOidc.vue";
import PhotoStream, { PhotoStreamArgs } from "./components/PhotoStream.vue";
// import { prepareDemoHandler } from './store/demo-data';
import {
  type ISearchParam,
  useAppStore,
  useCategoryStore,
  useCommentStore,
  useDomainStore,
  useMessageStore,
  useTopicStore,
  useUserStore,
} from "./store/index";
import type { ClientType, IDomainSetting, SetPayload } from "./store/domain-store";
import type { AuthorityGroupPolicy, ShowReactionDateType } from "@/support-acl-types";
import {
  CreateTopicAuthorityGroupPolicyDefault,
  ShowReactionDatePolicyDefault,
  ShowReactionDateTypeDefault,
  UpdateTopicAuthorityGroupPolicyDefault,
} from "@/support-acl-types";
import type { UpdateCommentPayload } from "./store";
import Loading from "./components/loading/Loading.vue";
import { LoadingManager } from "./components/loading/loading-manager";
import { EventManager } from "./events/event-manager";
import AlertModal from "./components/AlertModal.vue";
import HistoryUtility from "./history";
import NotAllowedModal from "./components/NotAllowedModal.vue";
import FavoriteModal from "./components/FavoriteModal.vue";
import AclEditModal from "./components/acl/AclEditModal.vue";
import type { DirectDomainMembersType } from "./direct-restapi-types";
import Acl from "./model/acl";
import { isSpecialPath } from "./direct-app-config";
import ErrorModal from "./components/ErrorModal.vue";
import ActionSheet from "./components/common/ActionSheet.vue";
import { AttachmentFileType, AttachmentPolicy, DownloadPolicy } from "./API";
import { CustomDeviceType, CustomFileType, RestrictType } from "./domain-setting-types";
import type { DownloadDeviceTypes } from "./suppport-attachment-types";
import { ServerApiAccess } from "./server-api-access";
import { IndexedDB } from "./indexeddb";
import SortCommentSetting from "./components/SortCommentSetting.vue";
import type { SORT_TYPE } from "./store";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

type DomainSettingArgs = {
  categories: Category[];
  domainId: string;
  me: User;
  initalCreateTopicAuthorityGroupPolicy: AuthorityGroupPolicy;
  initalUpdateTopicAuthorityGroupPolicy: AuthorityGroupPolicy;
  initalAttachRestrictType: RestrictType;
  initalAttachCustomFileType: CustomFileType[];
  initalDownloadRestrictType: RestrictType;
  initalDownloadCustomFileType: CustomDeviceType[];
  initalShowReactionDatePolicy: boolean;
  initalShowReactionDateType: ShowReactionDateType;
};

export default defineComponent({
  name: "app",
  components: {
    DomainSetting,
    LoginErrorOidc,
    Loading,
    AlertModal,
    PhotoStream,
    NotAllowedModal,
    FavoriteModal,
    AclEditModal,
    ErrorModal,
    ActionSheet,
    SortCommentSetting,
  },
  setup() {
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $route / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const loginErrorOidc = ref<boolean>(false);

    const me = ref<User>(User.createNotFoundUser());
    const sideMenu = ref<boolean>(true); //!< true: サイドメニューを表示可能

    // ロード中
    const loading = ref<boolean>(false); //!< true: ロード中表示
    const loadingMessage = ref<string>(""); //!< ロード中メッセージ
    const indicatorBackground = ref<string>("transparent"); // インジケーターの背景

    const photoStreamModal = ref<boolean>(false);
    const photoStreamArgs = ref<PhotoStreamArgs>({ files: [], messageId: "", commentId: "" });

    // downloadable
    const downloadable = computed((): string[] => {
      // ユーザーのroleを見て、ダウンロード可能かどうかを判断後 downloadTypeList を返す
      const domainId = useAppStore().domainId;
      const me = useUserStore().me as User | undefined;
      if (me && me.isDownloadableRole(domainId)) {
        return useDomainStore().getDownloadTypeList;
      } else {
        return [];
      }
    });

    const clientType = computed((): ClientType => {
      return useDomainStore().getClientType as ClientType;
    });

    const allowSaveAttachments = computed((): DownloadDeviceTypes => {
      return useDomainStore().getAllowSaveAttachments as DownloadDeviceTypes;
    });

    // いいね一覧の引数
    const favoriteModalArgs = ref<{
      id: string;
      userList: FavoriteModalUser[];
      messageId: string;
      commentId: string;
    }>({ id: "favorite-modal", userList: [], messageId: "", commentId: "" });

    // 禁止モーダルの引数
    const notAllowedModalArgs = ref<{
      msg: string;
      show: boolean;
      icon: string;
      isMobile: boolean;
    }>({
      msg: "",
      show: false,
      icon: "",
      isMobile: false,
    });

    const domainSettingArgs = ref<DomainSettingArgs>({
      categories: [],
      me: User.createNotFoundUser(),
      ...Domain.create({ domainId: "" }),
      initalCreateTopicAuthorityGroupPolicy: CreateTopicAuthorityGroupPolicyDefault,
      initalUpdateTopicAuthorityGroupPolicy: UpdateTopicAuthorityGroupPolicyDefault,
      initalAttachRestrictType: RestrictType.direct,
      initalAttachCustomFileType: [],
      initalDownloadRestrictType: RestrictType.omit,
      initalDownloadCustomFileType: [],
      initalShowReactionDatePolicy: ShowReactionDatePolicyDefault,
      initalShowReactionDateType: ShowReactionDateTypeDefault,
    });

    // コメントソート設定モーダルの引数
    const sortCommentSettingArgs = ref<{ originSortValue: SORT_TYPE }>({ originSortValue: "ASC" });

    /** aclモーダル用 */
    const aclEditModalArgs = ref<{
      id: string;
      acl: Acl;
      domainUsers: DirectDomainMembersType;
      targetId: string;
      targetType: "TOPIC" | "MESSAGE" | "COMMENT";
    }>({
      id: "acl-edit-modal",
      acl: Acl.createDummy(),
      domainUsers: { contents: [] },
      targetId: "",
      targetType: "TOPIC",
    });

    const alertModalArgs = ref<{
      msg: string;
      isHelp: boolean;
      helpContentArgs: { title: string; content: string; isError: boolean };
      hideCancel: boolean;
      clickedOK: () => void;
      clickedCancel: () => void;
      okLabel?: string;
      okVariant?: string;
    }>({
      msg: "",
      isHelp: false,
      helpContentArgs: { title: "", content: "", isError: true },
      hideCancel: false,
      clickedOK: () => {
        return;
      },
      clickedCancel: () => {
        return;
      },
    });
    const errorModalArgs = ref<{ msg: string; afterProcess: () => void }>({
      msg: "",
      afterProcess: () => {
        return;
      },
    });

    // lazyLoadを開始するフラグ
    const lazyLoad = ref<boolean>(false);

    let unsubscribeMutation: (() => void) | undefined; // Vuexの変更監視

    let nextProcess: (() => void) | undefined;

    const searchWord = computed((): string => {
      return useAppStore().searchParam.word;
    });

    const isMobile = computed((): boolean => {
      return useAppStore().isMobile;
    });

    // Vuex Actions
    const addCategory = (envelop: { data: Category }): void => {
      useCategoryStore().create(envelop as never);
    };
    const updateCategory = (envelope: Partial<Category>): void => {
      useCategoryStore().update(envelope as never);
    };
    const deleteCategory = (envelope: { domainId: string; id: string }): void => {
      useCategoryStore().delete(envelope as never);
    };
    const addTopic = (envelope: { topic: Topic }): void => {
      useTopicStore().create(envelope as never);
    };
    const updateTopic = (param: Partial<Topic>): void => {
      useTopicStore().update(param as never);
    };
    const deleteTopic = (param: Partial<Topic>): void => {
      useTopicStore().delete(param as never);
    };
    const setDomainId = (domainId: string): void => {
      useAppStore().setDomainId(domainId);
    };
    const setTopicId = (topicId: string): void => {
      useAppStore().setTopicId(topicId);
    };
    const addMessage = (envelope: { message: Message }): void => {
      useMessageStore().create(envelope as never);
    };
    const editMessage = (envelope: { messageId: string; param: Partial<Message> }): void => {
      useMessageStore().update(envelope as never);
    };
    const addComment = (envelope: { comment: Comment; messageeId: string }): void => {
      useCommentStore().create(envelope as never);
    };
    const editComment = (envelope: UpdateCommentPayload): void => {
      useCommentStore().update(envelope as never);
    };
    const setSearchParam = (param: ISearchParam): void => {
      useAppStore().setSearchParam(param);
    };
    const getSearchParam = (): Promise<ISearchParam> => {
      return useAppStore().getSearchParam();
    };
    const setDomainSetting = (setting: SetPayload): void => {
      useDomainStore().setDomainSetting(setting as never);
    };
    const getDomainSetting = (): IDomainSetting => {
      return useDomainStore().get;
    };
    const setNavigationGuard = (navigationGuard: boolean): void => {
      useAppStore().setNavigationGuard(navigationGuard);
    };

    // サイドメニューの表示状態を更新
    const updateSidemenuVisibility = (route: Route): void => {
      if (
        route.name == "edit-topic" ||
        route.name == "new-topic" ||
        route.name == "edit-message" ||
        route.name == "post-message" ||
        route.name == "maintenance"
      ) {
        sideMenu.value = false;
      } else {
        sideMenu.value = true;
      }
    };

    // PhotoStream close
    const closePhotoStream = (): void => {
      photoStreamArgs.value = { files: [], messageId: "", commentId: "" };
      photoStreamModal.value = false;
    };

    // on-app-close のハンドラーを登録
    const registerCloseAppHandler = (): void => {
      rootEvent.on("on-app-close", () => {
        if (window.directJsApi?.closeWindow != undefined) {
          window.directJsApi.closeWindow();
        }
        if (window.webkit?.messageHandlers?.DANHCloseWebView) {
          window.webkit.messageHandlers.DANHCloseWebView.postMessage("");
        }
      });
    };

    const leaveEvent = (): void => {
      setNavigationGuard(false);
      if (nextProcess) {
        nextProcess();
      }
    };

    const leaveCancel = (): void => {
      nextProcess = undefined;
    };

    // => CategoryEdit
    const saveCategoryEdit = (): void => {
      rootEvent.emit("save-category-edit");
    };

    // ページ遷移時のモーダルイベント
    // (各種モーダルのページ遷移時のイベントはここに入れる)
    const updatePage = (): void => {
      vm?.$bvModal.hide("favorite-modal"); // ページ遷移でfavoriteModal閉じる
    };

    // 組織IDがパス上に無い場合の解決
    const saveLastAccessPath = (): void => {
      let lastAccessPath = vm?.$route.path as string;

      // domain_idクエリがURLに入っているかチェック
      // 入っている場合は {protocol}://{host}/?domain_id={組織ID}&{その他クエリ} の形式
      // このため、/{組織ID}を lastAccessPath として保存する
      const domainId = vm?.$route.query.domain_id;
      if (typeof domainId == "string") {
        lastAccessPath = `/${domainId}`;
      }

      if (isSpecialPath(lastAccessPath) == false) {
        useAppStore().setLastAccessPath(lastAccessPath);
      }
    };

    const updateHtmlTitle = (): void => {
      // HTMLタイトル
      const domainName = useDomainStore().getDomainName as string;
      const title = (domainName ? `${domainName} | ` : "") + "掲示板";
      document.title = title;
    };

    /** 組織選択時の処理 */
    const onDomainSelected = (domainId: string): void => {
      const path = `/${domainId}`;
      if (path == vm?.$route.path) return; // 遷移先が同じ
      setDomainId(domainId);
      setTopicId(""); // topicIdのリセット
      vm?.$router.push(path);
    };

    /**
     * ユーザーが変更されたかどうかを判定する
     * @return true: 変更された。ただし新規の場合はfalse判定になります
     * @return false: 変更されてない。新規の場合もこちら
     */
    const checkUser = async (): Promise<boolean> => {
      const getUserId = (userId: unknown) => {
        // 配列だったら先頭を使う
        if (Array.isArray(userId)) userId = userId[0];
        if (typeof userId == "string" && 0 < userId.length) {
          return userId;
        } else {
          return undefined;
        }
      };

      const userId = getUserId(vm?.$route.query.user_id);
      if (!userId) return false; // userId無しの場合は以前のユーザーとみなす

      const old = getUserId(useAppStore().userId);
      await useAppStore().setUserId(userId);
      // 保存してからユーザーチェック
      // ここで保存してないと再ログイン時に以前のユーザーIDが残ったままになる
      if (old && userId && old != userId) {
        // 別ユーザーのログインとみなす。ログアウト
        vm?.$router.replace("/logout");
        return true;
      }
      return false;
    };

    // セッションやデータロード、イベントハンドラ設定 などを初回に行う（旧 created 相当）
    const created = (): void => {
      if (vm?.$route.path == "/maintenace") {
        return;
      }

      // cacheにsearchParamが保存され、searchQueryがついてない場合にsearchQueryがついたURLにreplace
      if (
        useAppStore().searchParam.word &&
        useAppStore().searchParam.word !== vm?.$route.query.search
      ) {
        let word = undefined;
        // search query とキャッシュ済み検索文がある場合は、 search query を優先する
        word = vm?.$route.query.search || useAppStore().searchParam.word;
        const query = {
          ...vm?.$route.query,
          search: word,
        };
        useAppStore()
          .setSearchParam({ word: word })
          .then(() => {
            vm?.$router.replace({ path: vm?.$route.path, query: query });
          });
      }

      // iOS向け設定
      global.DAJSHGetDomainId = () => {
        return useAppStore().domainId;
      };
      // Android向けツールバー設定
      if (window.directJsApi && typeof window.directJsApi.hideToolbar == "function") {
        window.directJsApi.hideToolbar();
      }

      // OIDC制限エラー時
      if (vm?.$route.path == "/login-error-oidc") {
        loginErrorOidc.value = true;
        HistoryUtility.init();
        registerCloseAppHandler();
        return;
      }

      EventManager.init(vm?.$root);

      // カテゴリーのハンドラ
      rootEvent.on("on-category-create", addCategory);
      rootEvent.on("on-category-update", updateCategory);
      rootEvent.on("on-category-delete", deleteCategory);

      // 話題の追加ハンドラ
      rootEvent.on("on-topic-create", addTopic);
      rootEvent.on("on-topic-update", updateTopic);
      rootEvent.on("on-topic-delete", deleteTopic);
      // 投稿の追加ハンドラ
      rootEvent.on("on-message-create", addMessage);
      // 投稿の更新ハンドラ
      rootEvent.on("on-message-edit", editMessage);

      rootEvent.on("on-comment-create", addComment); // コメントの追加ハンドラ
      rootEvent.on("on-comment-edit", editComment); // コメントの更新ハンドラ

      // 組織選択ハンドラ
      rootEvent.on("on-domain-selected", onDomainSelected);

      // 検索パラメータ用ハンドラ
      rootEvent.on("on-set-search-param", async (param: ISearchParam) => {
        setSearchParam(param);
      });
      rootEvent.on("on-get-search-param", getSearchParam);

      // Direct組織設定用ハンドラ
      rootEvent.on("on-set-domain-setting", async (payload: SetPayload) => {
        await setDomainSetting(payload);

        if (useDomainStore().isEnable(payload.domainId)) {
          // 利用不可が表示されていた場合、無効化する
          rootEvent.emit("on-not-allowed-modal", { show: false });
        } else {
          // 掲示板が無効設定されている
          rootEvent.emit("on-not-allowed-modal", { show: true });
        }
      });
      rootEvent.on("on-get-domain-setting", getDomainSetting);

      // photo-streamイベント
      rootEvent.on("open-photo-stream", (param: PhotoStreamArgs) => {
        photoStreamArgs.value = param;
        photoStreamModal.value = true;
      });

      // ロード中ハンドラ
      rootEvent.on(
        "on-loading",
        ({ value, msg, background }: { value: boolean; msg?: string; background?: string }) => {
          loading.value = value;
          loadingMessage.value = msg || "";
          indicatorBackground.value = background || "";

          useAppStore().setLoading(value);
        }
      );

      // アプリクローズハンドラを登録
      registerCloseAppHandler();

      // 再ログインハンドラ
      rootEvent.on("on-relogin", () => {
        vm?.$router.replace("/logout");
      });

      rootEvent.on("on-set-navigation-guard", setNavigationGuard);

      rootEvent.on("on-set-next-route-event", (next: () => void) => {
        nextProcess = next;
      });

      // 「掲示板の表示が禁止されている」モーダルの表示／非表示設定
      rootEvent.on("on-not-allowed-modal", async (args: { show: boolean }) => {
        const show = args.show;

        const errorMessage = show ? `この組織では利用できません。` : "";

        const isMobile = useAppStore().isMobile;

        const data = {
          icon: show ? "/img/icon/1152921507291203198.png" : "",
          msg: errorMessage,
          show: show,
          isMobile: isMobile,
        };
        notAllowedModalArgs.value = data;

        // モバイルUIの検索窓にoverlayをかける
        rootEvent.emit("on-overlay-with-search-window", args.show);
      });

      // いいね一覧の表示
      rootEvent.on(
        "on-favorite-modal",
        (args: { userList: FavoriteModalUser[]; messageId: string; commentId: string }) => {
          favoriteModalArgs.value.userList = args.userList;
          favoriteModalArgs.value.messageId = args.messageId;
          favoriteModalArgs.value.commentId = args.commentId;
          vm?.$bvModal.show("favorite-modal");
        }
      );

      rootEvent.on("show-domain-setting", () => {
        const domainId = useAppStore().domainId as string;
        const categories = useCategoryStore().get(domainId) as Category[];
        domainSettingArgs.value.categories = categories;
        domainSettingArgs.value.me = useUserStore().me as User;
        domainSettingArgs.value.domainId = domainId as string;

        const createTopicAuthorityGroupPolicy = useDomainStore()
          .getCreateTopicAuthorityGroupPolicy as AuthorityGroupPolicy;
        domainSettingArgs.value.initalCreateTopicAuthorityGroupPolicy =
          createTopicAuthorityGroupPolicy;

        const updateTopicAuthorityGroupPolicy = useDomainStore()
          .getUpdateTopicAuthorityGroupPolicy as AuthorityGroupPolicy;
        domainSettingArgs.value.initalUpdateTopicAuthorityGroupPolicy =
          updateTopicAuthorityGroupPolicy;

        const attachmentPolicy = useDomainStore().getAttachmentPolicy as AttachmentPolicy;
        switch (attachmentPolicy) {
          case AttachmentPolicy.DENY:
            domainSettingArgs.value.initalAttachRestrictType = RestrictType.omit;
            break;
          case AttachmentPolicy.ALLOW:
            domainSettingArgs.value.initalAttachRestrictType = RestrictType.permit;
            break;
          default:
            domainSettingArgs.value.initalAttachRestrictType = RestrictType.direct;
            break;
        }

        const attachmentFileTypes = useDomainStore().getAttachmentFileTypes as AttachmentFileType;
        domainSettingArgs.value.initalAttachCustomFileType = [];
        if (attachmentFileTypes.image)
          domainSettingArgs.value.initalAttachCustomFileType.push("image");
        if (attachmentFileTypes.movie)
          domainSettingArgs.value.initalAttachCustomFileType.push("movie");
        if (attachmentFileTypes.pdf) domainSettingArgs.value.initalAttachCustomFileType.push("pdf");
        if (attachmentFileTypes.office)
          domainSettingArgs.value.initalAttachCustomFileType.push("office");

        const downloadPolicy = useDomainStore().getDownloadPolicy;
        switch (downloadPolicy) {
          case DownloadPolicy.DENY:
            domainSettingArgs.value.initalDownloadRestrictType = RestrictType.omit;
            break;
          case DownloadPolicy.ALLOW:
            domainSettingArgs.value.initalDownloadRestrictType = RestrictType.permit;
            break;
          default:
            domainSettingArgs.value.initalDownloadRestrictType = RestrictType.direct;
            break;
        }

        const downloadFileTypes = useDomainStore().getDownloadFileTypes as DownloadDeviceTypes;
        domainSettingArgs.value.initalDownloadCustomFileType = [];
        if (downloadFileTypes.ios) domainSettingArgs.value.initalDownloadCustomFileType.push("ios");
        if (downloadFileTypes.web) domainSettingArgs.value.initalDownloadCustomFileType.push("web");
        if (downloadFileTypes.android)
          domainSettingArgs.value.initalDownloadCustomFileType.push("android");
        if (downloadFileTypes.desktop)
          domainSettingArgs.value.initalDownloadCustomFileType.push("desktop");

        const showReactionDatePolicy = useDomainStore().getShowReactionDatePolicy as boolean;
        domainSettingArgs.value.initalShowReactionDatePolicy = showReactionDatePolicy;
        const showReactionDateType = useDomainStore()
          .getShowReactionDateType as ShowReactionDateType;
        domainSettingArgs.value.initalShowReactionDateType = showReactionDateType;

        vm?.$bvModal.show("domain-setting");
      });

      rootEvent.on("show-sort-comment-setting", () => {
        const domainId = useAppStore().domainId;
        const originSortValue = useAppStore().getSortCommentSetting(domainId);
        sortCommentSettingArgs.value = { originSortValue };
        vm?.$bvModal.show("sort-comment-setting");
      });

      rootEvent.on(
        "open-acl-modal",
        async (param: { id: string; type: "TOPIC" | "MESSAGE" | "COMMENT"; acl: Acl }) => {
          const acl = param.acl;
          const targetId = param.id;
          const targetType = param.type;
          const domainUsers = useUserStore().getDomainMembers(useAppStore().domainId);
          aclEditModalArgs.value = {
            id: "acl-edit-modal",
            acl: acl,
            targetId: targetId,
            targetType: targetType,
            domainUsers: domainUsers,
          };
          vm?.$bvModal.show("acl-edit-modal");
        }
      );

      rootEvent.on("show-error-modal", (args: { msg: string; afterProcess: () => void }) => {
        errorModalArgs.value = args;
        vm?.$bvModal.show("error-modal");
      });

      // urlコピー失敗エラー
      rootEvent.on("failed-url-copy", () => {
        errorModalArgs.value = {
          msg: "URLコピーに失敗しました",
          afterProcess: () => {
            return;
          },
        };
        vm?.$bvModal.show("error-modal");
      });

      // 編集破棄アラート
      rootEvent.on("edit-discard-alert", () => {
        alertModalArgs.value = {
          msg: "編集内容を破棄します。\nよろしいですか?",
          isHelp: false,
          helpContentArgs: { title: "", content: "", isError: true },
          hideCancel: false,
          clickedOK: leaveEvent,
          clickedCancel: leaveCancel,
          okLabel: "破棄",
          okVariant: "danger",
        };
        vm?.$bvModal.show("alert-modal");
      });

      // 新規作成アラート
      rootEvent.on("free-alert", async () => {
        LoadingManager.start("free-alert", "通信中...");
        const api = new ServerApiAccess();
        const result = await api.getHelpContent();
        const helpContentArgs = result
          ? { title: result.title, content: result.content, isError: false }
          : { title: "", content: "", isError: true };
        LoadingManager.stop("free-alert");
        alertModalArgs.value = {
          msg: "",
          isHelp: true,
          helpContentArgs: helpContentArgs,
          hideCancel: true,
          clickedOK: () => {
            return;
          },
          clickedCancel: () => {
            return;
          },
        };
        vm?.$bvModal.show("alert-modal");
      });

      // 同期アラート
      rootEvent.on("sync-data-alert", () => {
        alertModalArgs.value = {
          msg: "サーバーと同期しますか?\n問題が生じた場合のみご利用ください。",
          isHelp: false,
          helpContentArgs: { title: "", content: "", isError: true },
          hideCancel: false,
          clickedOK: async () => {
            try {
              await useAppStore().syncLoad();
            } catch (error) {
              if (typeof error === "number" && error === 401) {
                // 認証エラー発生：再認証処理に流す
                EventManager.relogin();
              }
            }
          },
          clickedCancel: () => {
            return;
          },
          okLabel: "同期",
          okVariant: "danger",
        };
        vm?.$bvModal.show("alert-modal");
      });

      // ログアウト
      rootEvent.on("logout", () => {
        alertModalArgs.value = {
          msg: "ログアウトしますか?",
          isHelp: false,
          helpContentArgs: { title: "", content: "", isError: true },
          hideCancel: false,
          clickedOK: async () => {
            // direct トークン取り消し処理
            const revokeTokenPromise = async () => {
              const api = new ServerApiAccess();
              await api.revokeToken();
            };
            // DB削除
            const dropDBPromise = async () => {
              await IndexedDB.dropDB();
            };
            // サインアウト
            const signOutPromise = async () => {
              await AppAuth.signOut();
            };
            // 成功失敗に関わらず全て実行
            await Promise.allSettled([revokeTokenPromise(), dropDBPromise(), signOutPromise()]);
            // 画面を閉じる
            window.close();
          },
          clickedCancel: () => {
            return;
          },
          okLabel: "ログアウト",
          okVariant: "danger",
        };
        vm?.$bvModal.show("alert-modal");
      });

      // デモデータの用意
      // if( process.env.NODE_ENV == 'development' ) {
      //     prepareDemoHandler( this );
      // }

      // Query情報を覚えておく
      const query = vm?.$route.query;
      if (query) useAppStore().setFromQuery(query);

      // 検索ワードを覚えておく
      const search = (vm?.$route.query.search as string) || "";
      if (search) useAppStore().setSearchParam({ word: search });

      // デバイス情報を覚えておく
      const device = vm?.$route.query.device;
      if (device) useAppStore().setDevice(device);

      // invokerを覚えておく
      const invoker = vm?.$route.query.invokerapp;
      if (invoker) useAppStore().setInvokerApp(invoker);

      // 最終アクセスパスを覚えておく
      saveLastAccessPath();

      // Userチェック
      checkUser().then((changeUser) => {
        if (changeUser) {
          // ユーザーが変更された場合はログアウトするため、後続の処理は行わない
          // ロード中表示
          LoadingManager.init(vm?.$root);
          LoadingManager.start("startApp", "ロード中...");
        } else {
          // history
          HistoryUtility.init();

          LoadingManager.init(vm?.$root);

          //LoadingManager.start( "startApp", "ロード中..." );

          void nextTick(() => {
            lazyLoad.value = true;
          });
        }
      });
    };

    // 旧 created 相当（setup 本体で同期的に実行）
    created();

    onMounted(() => {
      // query.domain_idを指定された時
      if (vm?.$route.query.domain_id && typeof vm?.$route.query.domain_id == "string") {
        const domainId = vm?.$route.query.domain_id || "";
        vm?.$router.replace(domainId);
      }
      // マウント時に組織IDがパスに無い場合
      else if (vm?.$route.path == "/") {
        const lastAccess: string = useAppStore().lastAccessPath;
        const paths = lastAccess.split("/").filter((path) => !!path);
        if (paths.length > 0) {
          const searchParam: ISearchParam = useAppStore().searchParam;
          const query = searchParam.word ? { search: searchParam.word } : {};
          useAppStore().fetchData({ domainId: paths[0], topicId: paths[1] });
          vm?.$router.replace({ path: lastAccess, query: query });
        }
      }

      // Android向けツールバー設定
      if (window.directJsApi && typeof window.directJsApi.hideToolbar == "function") {
        window.directJsApi.hideToolbar();
      }

      updateHtmlTitle();

      if (!unsubscribeMutation) {
        // 旧 Vuex の setDirectDomainId mutation は setDomainId action からのみ commit されていた。
        // Pinia では setDomainId action を $onAction で捕捉し、domainId 設定後（after）に通知する。
        unsubscribeMutation = useAppStore().$onAction(({ name, args, after }) => {
          if (name !== "setDomainId") return;
          after(() => {
            // 組織選択イベント
            const domainId = args[0] as string;
            if (
              domainId &&
              window.directJsApi?.setCurrentDomainId &&
              isSpecialPath(domainId) == false
            ) {
              window.directJsApi.setCurrentDomainId(domainId);
            }
          });
        });
      }
    });

    onBeforeUpdate(() => {
      updatePage();
      updateSidemenuVisibility(vm?.$route as Route);
      updateHtmlTitle();
    });

    onBeforeUnmount(() => {
      if (unsubscribeMutation) unsubscribeMutation();
    });

    return {
      loginErrorOidc,
      me,
      sideMenu,
      loading,
      loadingMessage,
      indicatorBackground,
      photoStreamModal,
      photoStreamArgs,
      downloadable,
      clientType,
      allowSaveAttachments,
      favoriteModalArgs,
      notAllowedModalArgs,
      domainSettingArgs,
      sortCommentSettingArgs,
      aclEditModalArgs,
      alertModalArgs,
      errorModalArgs,
      lazyLoad,
      searchWord,
      isMobile,
      closePhotoStream,
      saveCategoryEdit,
    };
  },
});
</script>
